import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { UserDetail } from '../../models/user-detail';
import { Service } from '../../models/service';
import { EntitlementRequest, Entitlement } from '../../models/entitlement';
import { Client } from '../../models/client';

@Injectable({
  providedIn: 'root',
})
export class StoreService {
  // User ---------------------------------------------------------------------
  private readonly _user = new BehaviorSubject<UserDetail>(new UserDetail());
  readonly user$ = this._user.asObservable();

  get user(): UserDetail {
    return this._user.getValue();
  }

  private set user(val: UserDetail) {
    this._user.next(val);
  }

  assignUser(user: UserDetail) {
    this.user = user;
  }

  // Service ---------------------------------------------------------------------
  private readonly _services = new BehaviorSubject<Service[]>([]);
  readonly services$ = this._services.asObservable();

  get services(): Service[] {
    return this._services.getValue();
  }

  private set services(val: Service[]) {
    this._services.next(val);
  }

  initServices(data: Service[]) {
    this.services = data;
  }

  // Client ---------------------------------------------------------------------
  private readonly _clients = new BehaviorSubject<Client[]>([]);
  readonly clients$ = this._clients.asObservable();

  get clients(): Client[] {
    return this._clients.getValue();
  }

  private set clients(val: Client[]) {
    this._clients.next(val);
  }

  initClients(data: Client[]) {
    this.clients = data;
  }

  deleteClient(data: Client) {
    const clientArr: any[] = this._clients.getValue();

    clientArr.forEach((item, index) => {
      if (item === data) {
        clientArr.splice(index, 1);
      }
    });
    this._clients.next(clientArr);
  }

  // Entitlement ---------------------------------------------------------------------
  private readonly _entitlements = new BehaviorSubject<EntitlementRequest[]>(
    [],
  );
  readonly entitlements$ = this._entitlements.asObservable();

  get entitlements(): EntitlementRequest[] {
    return this._entitlements.getValue();
  }

  private set entitlements(val: EntitlementRequest[]) {
    this._entitlements.next(val);
  }

  initEntitlements(data: EntitlementRequest[]) {
    this.entitlements = data;
  }

  // Entitlement Service ---------------------------------------------------------------------
  private readonly _entitlementServices = new BehaviorSubject<Entitlement[]>(
    [],
  );
  readonly entitlementServices$ = this._entitlementServices.asObservable();

  get entitlementServices(): Entitlement[] {
    return this._entitlementServices.getValue();
  }

  private set entitlementServices(val: Entitlement[]) {
    this._entitlementServices.next(val);
  }

  initEntitlementServices(data: Entitlement[]) {
    if (data != null) {
      this.entitlementServices = data;
    }
  }

  createEntitlementServices(datas: Entitlement[]) {
    datas.forEach((data) => {
      let entitlementServices = this.entitlementServices.filter(
        (entService) => {
          if (
            entService.clientId === data.clientId &&
            entService.serviceId === data.serviceId
          ) {
            entService.entitlements.forEach((grp) => {
              if (grp.data[data.key] === data.value && grp.description) {
                grp.description[data.key] = data.description;
              }

              Object.getOwnPropertyNames(grp['data']).forEach((field) => {
                if (
                  grp['group']['groupId'] === '0' &&
                  data.entitlements != undefined &&
                  data.entitlements.some(
                    (a) => a['data'][field] === grp['data'][field],
                  )
                ) {
                  grp['group']['groupId'] = data.groupId;
                }
              });
            });
          }
          return entService;
        },
      );
      this.initEntitlementServices(entitlementServices);
    });
  }

  deleteEntitlementServices(datas: Entitlement[]) {
    datas.forEach((data) => {
      let entitlementServices = this.entitlementServices.filter(
        (entService) => {
          if (
            entService.clientId === data.clientId &&
            entService.serviceId === data.serviceId
          ) {
            entService.entitlements = entService.entitlements.filter(
              (grp) => grp['group']['groupId'] !== data.groupId,
            );
          }
          return entService;
        },
      );
      this.initEntitlementServices(entitlementServices);
    });
  }

  // Entitlement Client ---------------------------------------------------------------------
  private readonly _entitlementClients = new BehaviorSubject<Entitlement[]>([]);
  readonly entitlementClients$ = this._entitlementClients.asObservable();

  get entitlementClients(): Entitlement[] {
    return this._entitlementClients.getValue();
  }

  private set entitlementClients(val: Entitlement[]) {
    this._entitlementClients.next(val);
  }

  initEntitlementClients(data: Entitlement[]) {
    if (data != null) {
      this.entitlementClients = data;
    }
  }

  addClient(data: Client) {
    if (this.clients.filter((a) => a.id === data.id).length === 0) {
      this.clients.push(data);
    }
  }
}
